import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

const GRADE_COLORS = {
  elite: '#22c55e', good: '#86efac', neutral: '#94a3b8', tough: '#f97316', brutal: '#ef4444',
};

export default function LORMatchupPreviews({ api, status }) {
  const currentWeek = status?.current_week || 1;
  const [week, setWeek] = useState(currentWeek);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setWeek(currentWeek); }, [currentWeek]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${api}/api/lor/intel/matchups/preview/${week}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setPreviews(d || []); setLoading(false); })
      .catch(() => { setError('Could not load matchup previews.'); setLoading(false); });
  }, [api, week]);

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title">🧭 Matchup Previews</h2>
        <div className="lor-matchup-week-nav">
          {Array.from({ length: 17 }, (_, i) => i + 1).map(w => (
            <button
              key={w}
              className={`lor-week-btn${week === w ? ' active' : ''}`}
              onClick={() => setWeek(w)}
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      <div className="lor-intel-meta">
        Week {week} projections · <span className="lor-generated-badge">rules-based</span>
      </div>

      {loading && <div className="lor-loading">Loading matchup previews…</div>}
      {error && <div className="lor-empty">{error}</div>}

      {!loading && previews.length === 0 && !error && (
        <div className="lor-empty">No matchup data for week {week}. Season may not have started yet.</div>
      )}

      {!loading && previews.length > 0 && (
        <div className="lor-preview-grid">
          {previews.map(p => (
            <MatchupPreviewCard key={p.matchup_id} preview={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function MatchupPreviewCard({ preview }) {
  const { home, away, projected_winner, projected_margin, swing_player, h2h, playoff_implications, confidence } = preview;
  const winner = projected_winner === 'home' ? home : away;
  const loser  = projected_winner === 'home' ? away : home;

  return (
    <div className="lor-card lor-preview-card">
      {/* Header: projected winner */}
      <div className="lor-preview-header">
        <span className="lor-preview-projected-winner">
          Projected: <strong>{winner.display_name}</strong> by {projected_margin} pts
        </span>
        <span className={`lor-preview-conf lor-preview-conf--${confidence.toLowerCase()}`}>
          {confidence} confidence
        </span>
      </div>

      {/* Matchup sides */}
      <div className="lor-preview-matchup">
        <MatchupSide side={home} isWinner={projected_winner === 'home'} label="home" />
        <div className="lor-preview-vs">
          <span className="lor-preview-vs-text">vs</span>
        </div>
        <MatchupSide side={away} isWinner={projected_winner === 'away'} label="away" />
      </div>

      {/* Key players */}
      <div className="lor-preview-keys">
        <div className="lor-preview-key-section">
          <div className="lor-preview-key-label">Key players — {home.display_name}</div>
          {(home.key_players || []).map((p, i) => (
            <div key={i} className="lor-preview-key-player">
              <span className="lor-preview-key-pos">{p.position}</span>
              <span className="lor-preview-key-name">{p.full_name}</span>
              <span className="lor-preview-key-proj">{p.projected} pts</span>
            </div>
          ))}
        </div>
        <div className="lor-preview-key-section">
          <div className="lor-preview-key-label">Key players — {away.display_name}</div>
          {(away.key_players || []).map((p, i) => (
            <div key={i} className="lor-preview-key-player">
              <span className="lor-preview-key-pos">{p.position}</span>
              <span className="lor-preview-key-name">{p.full_name}</span>
              <span className="lor-preview-key-proj">{p.projected} pts</span>
            </div>
          ))}
        </div>
      </div>

      {/* Swing player */}
      {swing_player && (
        <div className="lor-preview-swing">
          <span className="lor-preview-swing-label"><Icon emoji="🎯" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Swing player:</span>
          <span className="lor-preview-swing-name">{swing_player.full_name}</span>
          <span className="lor-preview-swing-meta">{swing_player.position} · {swing_player.team}</span>
          <span className="lor-preview-swing-ceil">Ceiling: {swing_player.projected_high} pts</span>
        </div>
      )}

      {/* H2H history */}
      {(h2h.home_wins + h2h.away_wins) > 0 && (
        <div className="lor-preview-h2h">
          <span className="lor-preview-h2h-label">H2H:</span>
          <span>{home.display_name} {h2h.home_wins}–{h2h.away_wins} {away.display_name}</span>
          {h2h.last_winner_side && (
            <span className="lor-preview-h2h-last">
              · Last met: {h2h.last_winner_side === 'home' ? home.display_name : away.display_name} won
            </span>
          )}
        </div>
      )}

      {/* Playoff implications */}
      <div className="lor-preview-playoff-impl">
        <div className="lor-preview-impl-row">
          <span className="lor-preview-impl-side">{home.display_name}</span>
          <span className="lor-preview-impl-odds">{home.playoff_odds?.toFixed(0)}% playoff odds</span>
        </div>
        <div className="lor-preview-impl-row">
          <span className="lor-preview-impl-side">{away.display_name}</span>
          <span className="lor-preview-impl-odds">{away.playoff_odds?.toFixed(0)}% playoff odds</span>
        </div>
      </div>

      <div className="lor-generated-tag">⚙️ rules-based</div>
    </div>
  );
}

function MatchupSide({ side, isWinner, label }) {
  const avatarUrl = side.avatar
    ? `https://sleepercdn.com/avatars/thumbs/${side.avatar}`
    : null;
  const gradeColor = GRADE_COLORS[side.matchup_grade] || '#94a3b8';

  return (
    <div className={`lor-preview-side${isWinner ? ' lor-preview-side--winner' : ''}`}>
      {avatarUrl && (
        <img src={avatarUrl} alt={side.display_name} className="lor-avatar lor-avatar--md" />
      )}
      <div className="lor-preview-side-name">{side.display_name}</div>
      <div className="lor-preview-side-record">{side.wins}–{side.losses}</div>
      <div className="lor-preview-side-proj">{side.projected}</div>
      <div className="lor-preview-side-proj-label">projected</div>
      <div className="lor-preview-matchup-grade">
        <span className="lor-preview-grade-dot" style={{ background: gradeColor }} />
        <span className="lor-preview-grade-lbl">{side.matchup_grade_label} matchup</span>
      </div>
    </div>
  );
}
